/* eslint-disable no-underscore-dangle */
import { useRouter } from '@application/utils';
import type { ProGetProQuery } from '@gql/graphql';
import type { DetailListScheduleData } from '@presentation/components';
import { useMemo } from 'react';
import { daysOfWeek } from '@application/constants';

type Pro = NonNullable<ProGetProQuery['searchProfessionals']>['professionals'][number];

export const useFormatProSchedule = (pro: Pro | undefined) => {
  const { translate } = useRouter();
  const proSchedule = useMemo((): DetailListScheduleData | undefined => {
    if (!pro?.proSchedule?.length) return;
    if (!(pro?.proSchedule ?? []).filter((x) => (x?.avails ?? []).length)?.length) return;

    const scheduleDays = pro?.proSchedule?.map((x) => x?.dayOfWeek);
    if (!scheduleDays.length) return;

    return {
      times: new Array(7).fill(undefined).map((_, index) => {
        const dayOfWeek = daysOfWeek[index];
        if (scheduleDays.includes(dayOfWeek)) {
          const availabilities = pro.proSchedule
            .filter((x) => x?.dayOfWeek === dayOfWeek)
            .map((x) => x?.avails)
            .flat();
          return availabilities.map((availability) => {
            return `${availability?.start} - ${availability?.end}`;
          });
        }
        return [];
      }),
    };
  }, [pro?.proSchedule]);

  const shopSchedule = useMemo((): DetailListScheduleData | undefined => {
    if (!pro?.hasShop) return;
    if (!pro?.shopSchedule?.length) return;
    if (!(pro?.shopSchedule ?? []).filter((x) => (x?.openingHours ?? []).length)?.length) return;

    const scheduleDays = pro?.proSchedule?.map((x) => x?.dayOfWeek);
    if (!scheduleDays.length) return;

    return {
      times: new Array(7).fill(undefined).map((_, index) => {
        const dayOfWeek = daysOfWeek[index];
        if (scheduleDays.includes(dayOfWeek)) {
          const availabilities = pro.shopSchedule
            .filter((x) => x?.dayOfWeek === dayOfWeek)
            .map((x) => x?.openingHours)
            .flat();
          return availabilities.map((availability) => {
            return `${availability?.start} - ${availability?.end}`;
          });
        }
        return [];
      }),
    };
  }, [pro?.hasShop, pro?.proSchedule, pro?.shopSchedule]);

  const urgencyAvailabilities = useMemo((): DetailListScheduleData | undefined => {
    if (!pro?.urgencyAvailabilities?.length) return;
    const urgencyDays = pro?.urgencyAvailabilities?.map((x) => x.dayOfWeek);
    if (!urgencyDays.length) return;

    return {
      times: new Array(7).fill(undefined).map((_, index) => {
        const dayOfWeek = daysOfWeek[index];
        if (urgencyDays.includes(dayOfWeek)) {
          return [translate('glossary.available')];
        }
        return ['-'];
      }),
    };
  }, [pro?.urgencyAvailabilities, translate]);

  return {
    urgencyAvailabilities,
    proSchedule,
    shopSchedule,
  };
};
